import { MobileRecordState } from "../layout/MobileRecordState";
import { useCanAccess } from "ra-core";
import { useCreation } from "../misc/CreationContext";
import { PhoneNumber } from "../misc/PhoneNumber";
import {
  RecordContextProvider,
  ShowBase,
  useListContext,
  useRecordContext,
  useShowContext,
  useTranslate,
} from "ra-core";

import { Link, Link as RouterLink, useLocation } from "react-router";

import { UserPlus } from "lucide-react";

import { ReferenceManyField } from "@/components/admin/reference-many-field";

import { SortButton } from "@/components/admin/sort-button";

import { Button } from "@/components/ui/button";

import { Card, CardContent } from "@/components/ui/card";

import { Separator } from "@/components/ui/separator";

import { useIsMobile } from "@/hooks/use-mobile";

import { Avatar } from "../contacts/Avatar";

import { TagsList } from "../contacts/TagsList";

import { MobileContent } from "../layout/MobileContent";

import MobileHeader from "../layout/MobileHeader";

import { MobileBackButton } from "../misc/MobileBackButton";

import type { Company, Contact } from "../types";

import {
  AdditionalInfo,
  AddressInfo,
  CompanyAside,
  CompanyInfo,
  CompanySpecialization,
  OperationalInfo,
} from "./CompanyAside";

import { CompanyAvatar } from "./CompanyAvatar";

import { CompanyTagsList } from "./CompanyTagsList";

export const CompanyShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase queryOptions={{ onError: isMobile ? () => {} : undefined }}>
      {isMobile ? <CompanyShowContentMobile /> : <CompanyShowContent />}
    </ShowBase>
  );
};

const CompanyShowContentMobile = () => {
  const { record, isPending, refetch } = useShowContext<Company>();

  if (isPending || !record) {
    return (
      <MobileRecordState
        loading={isPending}
        backTo="/companies"
        title="Не удалось открыть компанию"
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/companies" />

        <div className="flex flex-1 min-w-0">
          <Link to="/companies" className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold">Компания</h1>
          </Link>
        </div>
      </MobileHeader>

      <MobileContent>
        <div data-mobile-reveal className="mobile-counterparty-hero mb-6">
          <div className="flex items-center">
            <CompanyAvatar />

            <div className="mx-3 flex-1 min-w-0">
              <h2 className="text-2xl font-bold break-words">{record.name}</h2>

              <div className="mt-2">
                <CompanyTagsList />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <CompanyInfo record={record} />

          <CompanySpecialization record={record} />

          <OperationalInfo record={record} />

          <AddressInfo record={record} />

          <AdditionalInfo record={record} />

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold">Контактные лица</h3>

              <CreateRelatedContactButton />
            </div>

            <Separator className="my-3" />

            <ReferenceManyField
              reference="contacts_summary"
              target="company_id"
              sort={{
                field: "last_name",
                order: "ASC",
              }}
              perPage={100}
            >
              <ContactsIterator />
            </ReferenceManyField>
          </div>
        </div>
      </MobileContent>
    </>
  );
};

const CompanyShowContent = () => {
  const { record, isPending } = useShowContext<Company>();

  if (isPending || !record) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col lg:flex-row pb-2 gap-6 min-w-0">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <div className="flex items-start gap-3 mb-6">
              <CompanyAvatar />

              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-semibold truncate">
                  {record.name}
                </h1>

                <div className="mt-2">
                  <CompanyTagsList />
                </div>
              </div>
            </div>

            <Separator />

            <div className="pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4 min-w-0">
                <div>
                  <h2 className="text-lg font-semibold">Контактные лица</h2>

                  <p className="text-sm text-muted-foreground">
                    Люди, связанные с этой компанией
                  </p>
                </div>

                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {record.nb_contacts ? (
                    <ReferenceManyField
                      reference="contacts_summary"
                      target="company_id"
                      sort={{
                        field: "last_name",
                        order: "ASC",
                      }}
                    >
                      <SortButton fields={["last_name", "first_name"]} />
                    </ReferenceManyField>
                  ) : null}

                  <CreateRelatedContactButton />
                </div>
              </div>

              <ReferenceManyField
                reference="contacts_summary"
                target="company_id"
                sort={{
                  field: "last_name",
                  order: "ASC",
                }}
                perPage={100}
              >
                <ContactsIterator />
              </ReferenceManyField>
            </div>
          </CardContent>
        </Card>
      </div>

      <CompanyAside />
    </div>
  );
};

const ContactsIterator = () => {
  const location = useLocation();

  const {
    data: contacts = [],
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();

  if (isPending)
    return (
      <p role="status" className="py-4 text-sm text-muted-foreground">
        Загрузка контактных лиц…
      </p>
    );
  if (error)
    return (
      <div role="alert" className="py-4 space-y-3">
        <p className="text-sm text-muted-foreground">
          Не удалось загрузить контактные лица.
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          Повторить загрузку
        </Button>
      </div>
    );

  if (contacts.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Контактных лиц пока нет
      </div>
    );
  }

  return (
    <div className="divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactRow contact={contact} from={location.pathname} />
        </RecordContextProvider>
      ))}
    </div>
  );
};

const ContactRow = ({
  contact,
  from,
}: {
  contact: Contact;

  from: string;
}) => {
  const primaryPhone = contact.phone_jsonb?.find((phone) =>
    phone.number?.trim(),
  )?.number;

  const primaryEmail = contact.email_jsonb?.find((email) =>
    email.email?.trim(),
  )?.email;

  return (
    <div className="flex items-center gap-3 py-3 px-1 rounded-md hover:bg-muted transition-colors">
      <Avatar />

      <div className="flex-1 min-w-0">
        <RouterLink
          to={`/contacts/${contact.id}/show`}
          state={{ from }}
          className="font-medium break-words block"
        >
          {`${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()}
        </RouterLink>

        {contact.title && (
          <div className="mt-0.5 text-sm text-muted-foreground">
            {contact.title}
          </div>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {primaryPhone && <PhoneNumber value={primaryPhone} />}

          {primaryEmail && <span className="break-all">{primaryEmail}</span>}
        </div>

        <div className="mt-1.5">
          <TagsList />
        </div>
      </div>
    </div>
  );
};

const CreateRelatedContactButton = () => {
  const access = useCanAccess({ resource: "contacts", action: "create" });
  const openCreation = useCreation();
  const translate = useTranslate();

  const company = useRecordContext<Company>();
  if (!access.canAccess) return null;
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/contacts/create"
        data-related-contact
        onClick={(event) => {
          if (openCreation) {
            event.preventDefault();
            openCreation({
              resource: "contacts",
              defaults: { company_id: company?.id },
            });
          }
        }}
        state={
          company
            ? {
                record: {
                  company_id: company.id,
                },
              }
            : undefined
        }
        className="flex min-w-0 flex-wrap items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />

        {translate("resources.contacts.action.add", {
          _: "Добавить контакт",
        })}
      </RouterLink>
    </Button>
  );
};
